import useConfiguration from '@store/hooks/useConfiguration';
import ky from 'ky';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ICheckoutCustomer } from '../types';

interface IProps {
  customer: ICheckoutCustomer;
  registerUrl?: string;
  authenticateUrl?: string;
}

export default function Widgets({ customer, authenticateUrl, registerUrl }: IProps) {
  const { xsrf } = useConfiguration();
  const { t } = useTranslation();

  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  async function onSignIn() {
    setIsSubmitting(true);
    if (!authenticateUrl || !userName || !password) return;

    const response = await ky.post(authenticateUrl, {
      json: { userName, password },
      headers: { 'X-CSRF-TOKEN': xsrf },
      throwHttpErrors: false,
    });

    if (!response.ok) {
      if (response.status === 400) {
        const validationErrors = await response.json<any>();
        Object.keys(validationErrors.errors).forEach(key => {
          const errorMessage = validationErrors.errors[key];
          if (key === '') setFormErrors(errorMessage);
        });
      }
    } else {
      location.reload();
    }
  }

  if ('customerId' in customer) {
    return <></>;
  }

  return (
    <div className="widgets">
      <div className="flex flex-col">
        <div className="w-full p-2 bg-primary text-white text-center font-medium">
          <span>Ak ste registrovaný, prihláste sa</span>
        </div>
        <div className="px-6 py-4 bg-gray-300 border border-t-0">
          <div className="max-w-sm w-full grid gap-y-2 mx-auto">
            {formErrors.length > 0 && (
              <ul className="validation-summary-errors">
                {formErrors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            )}
            <div className="form-fields">
              <div className="form-field">
                <input
                  type="text"
                  name="login.username"
                  autoComplete="username"
                  placeholder={t('UserName')}
                  value={userName}
                  className="w-full"
                  onKeyDown={async e => {
                    if (e.code !== 'Enter') return;
                    await onSignIn();
                  }}
                  onChange={e => {
                    setIsSubmitting(false);
                    setUserName(e.target.value);
                  }}
                />
                {isSubmitting && !userName && (
                  <span className="field-validation-error">{t('Form.UserName.NotEmpty')}</span>
                )}
              </div>

              <div className="form-field">
                <input
                  type="password"
                  name="login.password"
                  autoComplete="off"
                  value={password}
                  placeholder={t('Password')}
                  className="w-full"
                  onKeyDown={async e => {
                    if (e.code !== 'Enter') return;
                    await onSignIn();
                  }}
                  onChange={e => {
                    setIsSubmitting(false);
                    setPassword(e.target.value);
                  }}
                />
                {isSubmitting && !password && (
                  <span className="field-validation-error">{t('Form.Password.NotEmpty')}</span>
                )}
              </div>
            </div>
            <button
              className="btn w-full block bg-gray-800 hover:bg-black rounded-none text-white text-center mt-4"
              type="button"
              onClick={onSignIn}>
              {t('ToSignIn')}
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="w-full p-2 bg-gray-700 text-white text-center font-medium">
          <span>Ake sú výhody registrácie?</span>
        </div>
        <div className="px-6 py-4 bg-gray-300 border border-t-0">
          <div className="max-w-sm w-full flex flex-col gap-2 mx-auto">
            <div className="flex flex-col">
              <div className="text-center">
                <span className="icon text-green-500">
                  <i className="fa-solid fa-check-circle"></i>
                </span>
                <span className="ml-3">História objednávok a dodania tovaru</span>
              </div>
              <div className="text-center">
                <span className="icon text-green-500">
                  <i className="fa-solid fa-check-circle"></i>
                </span>
                <span className="ml-3">Sledovanie stavu objednaného tovaru</span>
              </div>
              <div className="text-center">
                <span className="icon text-green-500">
                  <i className="fa-solid fa-check-circle"></i>
                </span>
                <span className="ml-3">Vernostný program</span>
              </div>
            </div>

            <a
              className="btn w-full block bg-gray-800 hover:bg-black rounded-none text-white text-center mt-4"
              href={registerUrl}>
              {t('ToRegister')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
